import React, { useState, useEffect, useCallback, useRef } from 'react';
import { XMarkIcon, UserPlusIcon, UserIcon } from '@heroicons/react/24/outline';
import tenantUserService, { AvailableUser, CreateTenantUserData } from '../services/tenantUserService';
import userService, { UserCreate } from '../services/users';
import ErrorDisplay from './ErrorDisplay';
import SuccessMessage from './SuccessMessage';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useSuccessMessage } from '../hooks/useSuccessMessage';

// Types améliorés et plus stricts
type UserRole = 'TENANT_ADMIN' | 'TENANT_MANAGER';
type ModalMode = 'select' | 'create';

interface NewUserForm {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  locale: string;
}

interface RoleOption {
  value: UserRole;
  label: string;
  description: string;
}

const ROLE_OPTIONS: RoleOption[] = [
  { 
    value: 'TENANT_ADMIN', 
    label: 'Administrateur du tenant', 
    description: 'Accès complet à tous les sites du tenant' 
  },
  { 
    value: 'TENANT_MANAGER', 
    label: 'Gestionnaire du tenant', 
    description: 'Accès de gestion aux sites du tenant' 
  }
];

// Fonctions utilitaires
const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePhone = (phone: string): boolean => {
  if (!phone) return true; // Optionnel
  const phoneRegex = /^\+225[0-9]{10}$/;
  return phoneRegex.test(phone);
};

const formatPhone = (phone: string): string => {
  if (!phone) return '';
  // Nettoyer et formater le numéro
  const cleaned = phone.replace(/[^0-9+]/g, '');
  if (cleaned.startsWith('225')) {
    return `+${cleaned}`;
  } else if (cleaned.startsWith('0')) {
    return `+225${cleaned}`;
  }
  return cleaned;
};

const safeParseInt = (value: string | number): number => {
  if (typeof value === 'number') return value;
  const parsed = parseInt(value, 10);
  if (isNaN(parsed)) {
    throw new Error('ID utilisateur invalide');
  }
  return parsed;
};

interface AddTenantUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: number;
  tenantName: string;
  onUserAdded: () => void;
}

interface GeneratedCredentials {
  username: string;
  tempPassword: string;
  email: string;
  emailSent: boolean;
}

interface FormValidation {
  isValid: boolean;
  errors: Record<string, string>;
}

const AddTenantUserModal: React.FC<AddTenantUserModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  tenantName,
  onUserAdded
}) => {
  // États principaux
  const [availableUsers, setAvailableUsers] = useState<AvailableUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedRole, setSelectedRole] = useState<UserRole>('TENANT_ADMIN');
  const [notes, setNotes] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // États pour la création d'utilisateur
  const [mode, setMode] = useState<ModalMode>('select');
  const [newUser, setNewUser] = useState<NewUserForm>({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    locale: 'fr'
  });
  
  // États de validation
  const [validation, setValidation] = useState<FormValidation>({
    isValid: false,
    errors: {}
  });
  
  // État pour les credentials générés
  const [generatedCredentials, setGeneratedCredentials] = useState<GeneratedCredentials | null>(null);
  const [showCredentials, setShowCredentials] = useState(false);
  
  // Hooks personnalisés
  const { error, clearError, handleAsyncError } = useErrorHandler();
  const { message: successMessage, clearSuccess, showSuccess } = useSuccessMessage();
  
  // Ref pour éviter les fuites mémoire
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Fonction de validation du formulaire
  const validateForm = useCallback((): FormValidation => {
    const errors: Record<string, string> = {};
    
    if (mode === 'create') {
      if (!newUser.first_name.trim()) {
        errors.first_name = 'Le prénom est obligatoire';
      }
      if (!newUser.last_name.trim()) {
        errors.last_name = 'Le nom est obligatoire';
      }
      if (!newUser.username.trim()) {
        errors.username = 'Le nom d\'utilisateur est obligatoire';
      } else if (newUser.username.length < 3) {
        errors.username = 'Le nom d\'utilisateur doit contenir au moins 3 caractères';
      }
      if (!newUser.email.trim()) {
        errors.email = 'L\'email est obligatoire';
      } else if (!validateEmail(newUser.email)) {
        errors.email = 'Format d\'email invalide';
      }
      if (newUser.phone && !validatePhone(formatPhone(newUser.phone))) {
        errors.phone = 'Format de téléphone invalide. Utilisez le format +225XXXXXXXXXX (10 chiffres)';
      }
    } else {
      if (!selectedUserId) {
        errors.user = 'Veuillez sélectionner un utilisateur';
      }
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }, [mode, newUser, selectedUserId]);
  
  // Validation en temps réel
  useEffect(() => {
    const validationResult = validateForm();
    setValidation(validationResult);
  }, [validateForm]);
  
  // Validation en temps réel quand les champs changent
  useEffect(() => {
    const validationResult = validateForm();
    setValidation(validationResult);
  }, [mode, newUser, selectedUserId, validateForm]);
  
  // Charger les utilisateurs disponibles
  useEffect(() => {
    if (isOpen && tenantId) {
      loadAvailableUsers();
    }
  }, [isOpen, tenantId]);
  
  // Nettoyer les timeouts au démontage
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const loadAvailableUsers = useCallback(async () => {
    if (!tenantId) return;
    
    setLoadingUsers(true);
    try {
      const users = await tenantUserService.getAvailableUsers(tenantId);
      setAvailableUsers(users || []);
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
      setAvailableUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [tenantId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation finale
    const validationResult = validateForm();
    if (!validationResult.isValid) {
      setValidation(validationResult);
      return;
    }
    
    setLoading(true);
    
    const result = await handleAsyncError(async () => {
      let userId: number;
      let hasCredentials = false;

      if (mode === 'create') {
        // Créer un nouvel utilisateur
        const userData: UserCreate = {
          username: newUser.username.trim(),
          email: newUser.email.trim(),
          name: `${newUser.first_name.trim()} ${newUser.last_name.trim()}`,
          phone: newUser.phone ? formatPhone(newUser.phone) : undefined,
          locale: newUser.locale,
          sites: []
        };

        const createdUser = await userService.createUser(userData);
        userId = safeParseInt(createdUser.user.id);
        
        // Stocker les credentials générés si disponibles
        if (createdUser.tempPassword) {
          setGeneratedCredentials({
            username: createdUser.user.username,
            tempPassword: createdUser.tempPassword,
            email: createdUser.user.email || '',
            emailSent: createdUser.emailSent || false
          });
          hasCredentials = true;
        }
      } else {
        // Utiliser un utilisateur existant
        const selectedUser = await userService.selectUser(selectedUserId!);
        userId = safeParseInt(selectedUser.id);
      }

      // Ajouter l'utilisateur au tenant
      const data: CreateTenantUserData = {
        user_id: userId,
        tenant_id: tenantId,
        role: selectedRole,
        notes: notes.trim() || undefined,
        expires_at: expiresAt || undefined
      };

      await tenantUserService.addUserToTenant(data);
      
      return { success: true, hasCredentials };
    });
    
    if (result && result.success) {
      // Appeler le callback immédiatement pour recharger les données
      onUserAdded();
      
      if (mode === 'create' && result.hasCredentials) {
        // Afficher les credentials générés
        setShowCredentials(true);
        showSuccess('Utilisateur créé et ajouté au tenant avec succès !', true, 5000);
        // Ne pas fermer le modal automatiquement pour laisser le temps de voir les credentials
      } else {
        // Afficher le message de succès normal
        const successMsg = mode === 'create' 
          ? 'Utilisateur créé et ajouté au tenant avec succès !'
          : 'Utilisateur ajouté au tenant avec succès !';
        
        showSuccess(successMsg, true, 3000);
        
        // Réinitialiser le formulaire
        resetForm();
        
        // Attendre avant de fermer pour laisser le temps de voir le message de succès
        timeoutRef.current = setTimeout(() => {
          onClose();
        }, 1000);
      }
    }
    
    setLoading(false);
  };
  
  // Fonction pour copier le mot de passe
  const copyPassword = async () => {
    if (generatedCredentials?.tempPassword) {
      try {
        await navigator.clipboard.writeText(generatedCredentials.tempPassword);
        showSuccess('Mot de passe copié dans le presse-papiers !', true, 2000);
      } catch (err) {
        // Fallback pour les navigateurs plus anciens
        const textArea = document.createElement('textarea');
        textArea.value = generatedCredentials.tempPassword;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        showSuccess('Mot de passe copié dans le presse-papiers !', true, 2000);
      }
    }
  };

  const resetForm = useCallback(() => {
    setSelectedUserId(null);
    setSelectedRole('TENANT_ADMIN');
    setNotes('');
    setExpiresAt('');
    setMode('select');
    setNewUser({
      username: '',
      email: '',
      first_name: '',
      last_name: '',
      phone: '',
      locale: 'fr'
    });
    setValidation({ isValid: false, errors: {} });
  }, []);

  const handleClose = useCallback(() => {
    clearError();
    clearSuccess();
    setGeneratedCredentials(null);
    setShowCredentials(false);
    resetForm();
    
    // Annuler le timeout s'il existe
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    onClose();
  }, [clearError, clearSuccess, resetForm, onClose]);

  const handleCloseAfterCredentials = useCallback(() => {
    setShowCredentials(false);
    setGeneratedCredentials(null);
    resetForm();
    onClose();
  }, [resetForm, onClose]);
  
  const handlePhoneChange = useCallback((phone: string) => {
    const formatted = formatPhone(phone);
    setNewUser(prev => ({ ...prev, phone: formatted }));
  }, []);
  
  const handleModeChange = useCallback((newMode: ModalMode) => {
    setMode(newMode);
    // Réinitialiser la validation lors du changement de mode
    setValidation({ isValid: false, errors: {} });
    setGeneratedCredentials(null);
    setShowCredentials(false);
    // Effacer les messages lors du changement de mode
    clearError();
    clearSuccess();
  }, [clearError, clearSuccess]);

  // Effacer les messages lors de l'ouverture du modal
  useEffect(() => {
    if (isOpen) {
      clearError();
      clearSuccess();
    }
  }, [isOpen, clearError, clearSuccess]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
              <UserPlusIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Ajouter un utilisateur</h2>
              <p className="text-gray-600">au tenant {tenantName}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Affichage des credentials générés */}
        {showCredentials && generatedCredentials && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-green-800">Utilisateur créé avec succès !</h3>
                <p className="text-green-600">Les identifiants de connexion ont été générés</p>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="bg-white p-3 rounded border">
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom d'utilisateur</label>
                <div className="flex items-center">
                  <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm font-mono">
                    {generatedCredentials.username}
                  </code>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(generatedCredentials.username);
                      showSuccess('Nom d\'utilisateur copié !', true, 2000);
                    }}
                    className="ml-2 px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm transition-colors"
                    title="Copier le nom d'utilisateur"
                  >
                    📋
                  </button>
                </div>
              </div>
              
              <div className="bg-white p-3 rounded border">
                <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe temporaire</label>
                <div className="flex items-center">
                  <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm font-mono">
                    {generatedCredentials.tempPassword}
                  </code>
                  <button
                    type="button"
                    onClick={copyPassword}
                    className="ml-2 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm transition-colors"
                    title="Copier le mot de passe"
                  >
                    📋 Copier
                  </button>
                </div>
              </div>
              
              <div className="bg-white p-3 rounded border">
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <code className="block bg-gray-100 px-3 py-2 rounded text-sm font-mono">
                  {generatedCredentials.email}
                </code>
              </div>
              
              <div className="flex items-center text-sm">
                {generatedCredentials.emailSent ? (
                  <div className="flex items-center text-green-600">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Email envoyé avec succès
                  </div>
                ) : (
                  <div className="flex items-center text-yellow-600">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    Email non envoyé
                  </div>
                )}
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div>
                  <p className="text-sm text-yellow-800 font-medium">Important :</p>
                  <p className="text-sm text-yellow-700 mt-1">
                    L'utilisateur doit changer ce mot de passe lors de sa première connexion. 
                    Conservez ces informations en lieu sûr.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-4 flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCloseAfterCredentials}
                className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Fermer
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCredentials(false);
                  setGeneratedCredentials(null);
                  resetForm();
                }}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
              >
                Créer un autre utilisateur
              </button>
            </div>
          </div>
        )}

        {/* Boutons de basculement */}
        <div className="mb-6">
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleModeChange('select');
              }}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                mode === 'select'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center justify-center">
                <UserIcon className="h-4 w-4 mr-2" />
                Utilisateur existant
              </div>
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleModeChange('create');
              }}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                mode === 'create'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center justify-center">
                <UserPlusIcon className="h-4 w-4 mr-2" />
                Créer un utilisateur
              </div>
            </button>
          </div>
        </div>

        <ErrorDisplay 
          error={error} 
          onDismiss={clearError}
          title="Erreur lors de l'ajout de l'utilisateur"
          className="mb-4"
        />

        <SuccessMessage 
          message={successMessage || ''} 
          onDismiss={clearSuccess}
          title="Succès"
          variant="success"
          className="mb-4"
          autoHide={true}
          autoHideDelay={3000}
        />

        {!showCredentials && (
          <form onSubmit={handleSubmit} className="space-y-6">
          {mode === 'select' ? (
            /* Sélection de l'utilisateur existant */
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Utilisateur à ajouter
              </label>
              {loadingUsers ? (
                <div className="flex items-center text-sm text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                  Chargement des utilisateurs disponibles...
                </div>
              ) : availableUsers.length === 0 ? (
                <div className="text-sm text-gray-500">Aucun utilisateur disponible</div>
              ) : (
                <div>
                  <select
                    value={selectedUserId || ''}
                    onChange={(e) => setSelectedUserId(Number(e.target.value))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      validation.errors.user ? 'border-red-300' : 'border-gray-300'
                    }`}
                    required
                  >
                    <option value="">Sélectionner un utilisateur</option>
                    {availableUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {(user.first_name || 'Prénom')} {(user.last_name || 'Nom')} (@{user.username || 'username'}) - {user.email || 'email'}
                      </option>
                    ))}
                  </select>
                  {validation.errors.user && (
                    <p className="text-red-500 text-xs mt-1">{validation.errors.user}</p>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* Formulaire de création d'utilisateur */
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prénom *
                  </label>
                  <input
                    type="text"
                    value={newUser.first_name}
                    onChange={(e) => setNewUser({...newUser, first_name: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      validation.errors.first_name ? 'border-red-300' : 'border-gray-300'
                    }`}
                    required
                  />
                  {validation.errors.first_name && (
                    <p className="text-red-500 text-xs mt-1">{validation.errors.first_name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom *
                  </label>
                  <input
                    type="text"
                    value={newUser.last_name}
                    onChange={(e) => setNewUser({...newUser, last_name: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      validation.errors.last_name ? 'border-red-300' : 'border-gray-300'
                    }`}
                    required
                  />
                  {validation.errors.last_name && (
                    <p className="text-red-500 text-xs mt-1">{validation.errors.last_name}</p>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom d'utilisateur *
                </label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    validation.errors.username ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                />
                {validation.errors.username && (
                  <p className="text-red-500 text-xs mt-1">{validation.errors.username}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    validation.errors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                />
                {validation.errors.email && (
                  <p className="text-red-500 text-xs mt-1">{validation.errors.email}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Téléphone (optionnel)
                </label>
                <input
                  type="tel"
                  value={newUser.phone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  placeholder="+225XXXXXXXX"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    validation.errors.phone ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Format attendu : +225 suivi de 8 chiffres (ex: +2250700000000)
                </p>
                {validation.errors.phone && (
                  <p className="text-red-500 text-xs mt-1">{validation.errors.phone}</p>
                )}
              </div>
            </div>
          )}

          {/* Sélection du rôle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rôle dans le tenant
            </label>
            <div className="space-y-2">
              {ROLE_OPTIONS.map((option) => (
                <label key={option.value} className="flex items-start">
                  <input
                    type="radio"
                    name="role"
                    value={option.value}
                    checked={selectedRole === option.value}
                    onChange={(e) => setSelectedRole(e.target.value as UserRole)}
                    className="mt-1 mr-3"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">{option.label}</div>
                    <div className="text-sm text-gray-500">{option.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Date d'expiration (optionnel) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date d'expiration (optionnel)
            </label>
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Si spécifiée, l'utilisateur perdra l'accès après cette date
            </p>
          </div>

          {/* Notes (optionnel) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (optionnel)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Notes additionnelles sur cette assignation..."
            />
          </div>

          {/* Boutons d'action */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClose();
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading || !validation.isValid}
              className={`px-4 py-2 rounded-lg transition-colors ${
                loading || !validation.isValid
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
              title={!validation.isValid ? 'Veuillez remplir tous les champs obligatoires' : ''}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {mode === 'create' ? 'Création en cours...' : 'Ajout en cours...'}
                </div>
              ) : (
                mode === 'create' ? 'Créer et ajouter l\'utilisateur' : 'Ajouter l\'utilisateur'
              )}
            </button>
            
            {/* Indicateur de validation */}
            {!validation.isValid && !loading && (
              <div className="text-sm text-gray-500 text-center mt-2">
                {Object.keys(validation.errors).length > 0 ? (
                  <span>Veuillez corriger les erreurs ci-dessus</span>
                ) : (
                  <span>Veuillez remplir tous les champs obligatoires</span>
                )}
              </div>
            )}
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default AddTenantUserModal;

